/* eslint react/prop-types: 0 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography
} from '@mui/material';
import ApiService from '../../common/ApiCalls';
import HistoricalCard from './HistoricalCard';
import { historyPermissions } from './historyCorrections';

const labels = {
  asset: 'Location',
  entity: 'Entity',
  party: 'People',
  service: 'Services',
  contract: 'Accounts'
};

function HistoricalCards({ layout, rows, ...props }) {
  if (!layout?.cards?.length) return <Typography>No layout fields available.</Typography>;
  return layout.cards.map((card, index) => <HistoricalCard key={card.id || index} card={card} rows={rows} {...props} />);
}

export default function HistoricalLayout({
  id,
  date,
  timezone,
  objectType,
  subjectType = 'asset',
  canEditSubject
}) {
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [notice, setNotice] = useState(null);
  const key = `${subjectType}:${id}:${date}:${timezone}:${objectType}:${attempt}`;
  useEffect(() => {
    let active = true;
    const api = ApiService();
    const load = async () => {
      try {
        const data = await api.getLayoutHistory(id, {
          date,
          timezone,
          objectType,
          subjectType
        });
        const members = await Promise.all(
          data.assignments.map(async (assignment) => {
            if (!assignment.object_available)
              return { ...assignment, name: 'Unavailable record' };
            try {
              const fetchers = {
                party: api.getPartyById,
                service: api.getServicesById,
                contract: api.getContractById
              };
              const response = await fetchers[assignment.object_type](
                assignment.object_id
              );
              const record =
                assignment.object_type === 'contract'
                  ? response?.data?.data
                  : response;
              if (!record?.id) throw new Error('Unavailable identity');
              const name =
                assignment.object_type === 'party'
                  ? [record.first_name, record.last_name]
                      .filter(Boolean)
                      .join(' ')
                  : record.name;
              return {
                ...assignment,
                name: name || labels[assignment.object_type]
              };
            } catch {
              return {
                ...assignment,
                object_available: false,
                name: 'Unavailable record'
              };
            }
          })
        );
        if (active) setResult({ key, data, members });
      } catch (error) {
        const code = error?.response?.data?.errors?.[0]?.code;
        if (active)
          setResult({
            key,
            error:
              code === 'history_timezone_not_configured'
                ? 'History is not configured for this record yet.'
                : 'Unable to load historical data. Please try again.'
          });
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [id, date, timezone, objectType, subjectType, key]);

  if (result?.key !== key)
    return (
      <Box role="status" sx={{ p: 3 }}>
        <CircularProgress size={24} /> Loading historical data…
      </Box>
    );
  if (result.error)
    return (
      <Alert
        severity="error"
        action={
          <Button onClick={() => setAttempt((value) => value + 1)}>
            Retry
          </Button>
        }
      >
        {result.error}
      </Alert>
    );
  const { data, members } = result;
  const layout = data.layouts.find((item) => item.object_type === objectType);
  const reload = () => {
    setAttempt((value) => value + 1);
  };
  return (
    <>
      {notice && (
        <Alert
          severity="success"
          onClose={() => setNotice(null)}
          sx={{ mb: 2 }}
        >
          {notice}
        </Alert>
      )}
      <Alert severity="info" sx={{ mb: 2 }}>
        Viewing {date}. Layouts and labels use their current configuration.
      </Alert>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {labels[objectType]}
      </Typography>
      {objectType === subjectType ? (
        <HistoricalCards
          layout={layout}
          rows={data.values.filter((row) => row.object_type === objectType)}
          canEdit={canEditSubject ?? historyPermissions(id, objectType).edit}
          context={data.context} subjectId={id} onReload={reload} onSaved={(message) => { setNotice(message); reload(); }}
        />
      ) : members.length === 0 ? (
        <Typography>No assignments on this date.</Typography>
      ) : (
        members.map((member) => (
          <Box component="section" key={member.id} sx={{ mb: 3 }}>
            <Typography variant="h6">{member.name}</Typography>
            {member.object_available ? (
              <HistoricalCards
                layout={layout}
                rows={data.values.filter(
                  (row) => row.assignment_period_id === member.id
                )}
                canEdit={
                  historyPermissions(id, member.object_type, member.object_id)
                    .edit
                }
                context={data.context} subjectId={id} onReload={reload} onSaved={(message) => { setNotice(message); reload(); }}
              />
            ) : (
              <Typography>This record is no longer available.</Typography>
            )}
          </Box>
        ))
      )}
    </>
  );
}
